import { visionLabApi } from '@/app/apiClient';
import { toAssetCatalogSearchParams } from '@/features/assets/lib/assetCatalogSearchParams';
import type {
  AssetCatalogFilters,
  AssetPreview,
  AssetPreviewStart,
  GlobalAssetsPage,
  ProjectAsset,
  RejectAssetInput,
} from '@/features/assets/types/asset';
import type { ApiCallOptions } from '@/shared/api/client';

/** Fetch one authorized asset by id and validate its media-discriminated metadata. */
export async function fetchAsset(
  assetId: string,
  options: ApiCallOptions = {},
): Promise<ProjectAsset> {
  return visionLabApi.get<ProjectAsset>(`/assets/${encodeURIComponent(assetId)}`, options);
}

/**
 * Accepted-asset catalog (`GET /assets`) across every readable project. Optional
 * filters are AND-composed by the backend and omitted entirely when unset.
 *
 * Sorting is an inseparable `sort`/`order` pair. The cursor remains an opaque
 * backend token and is passed verbatim through the API client's search serializer.
 */
export async function fetchAssetCatalog(
  filters: AssetCatalogFilters = {},
  options: ApiCallOptions = {},
): Promise<GlobalAssetsPage> {
  return visionLabApi.get<GlobalAssetsPage>('/assets', {
    ...options,
    searchParams: toAssetCatalogSearchParams(filters),
  });
}

/** Terminally rejects one Asset; actor and timestamp are derived by the backend. */
export async function rejectAsset(assetId: string, input: RejectAssetInput): Promise<ProjectAsset> {
  return visionLabApi.post<ProjectAsset>(`/assets/${encodeURIComponent(assetId)}/reject`, {
    json: input,
  });
}

function assetPreviewPath(assetId: string): string {
  return `/assets/${encodeURIComponent(assetId)}/preview`;
}

/** Starts or returns the current derived Asset preview task. */
export async function startAssetPreview(
  assetId: string,
  options: ApiCallOptions = {},
): Promise<AssetPreviewStart> {
  return visionLabApi.post<AssetPreviewStart>(assetPreviewPath(assetId), { ...options });
}

/** Reads the current task and, when ready, fresh short-lived playback URLs. */
export async function fetchAssetPreview(
  assetId: string,
  options: ApiCallOptions = {},
): Promise<AssetPreview> {
  return visionLabApi.get<AssetPreview>(assetPreviewPath(assetId), {
    ...options,
    cache: 'no-store',
  });
}
